import {
  ComponentClass,
  createElement,
  FocusEvent,
  forwardRef,
  FunctionComponent,
  HtmlHTMLAttributes,
  InputHTMLAttributes,
  MouseEvent as ReactMouseEvent,
  NamedExoticComponent,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import classnames from "classnames";
import { v4 } from "uuid";
import { RefType } from "../../../../types";
import { genericMemo } from "../../../../utils";
import ClearButton from "./ClearButton";
import ErrorButton from "./ErrorButton";

import styles from "./style.module.scss";

const InputComponent = <P extends object = InputHTMLAttributes<HTMLInputElement>, R extends unknown = HTMLInputElement>(props: InputProps<P, R>) => <></>;
type InputComponentType = typeof InputComponent;

type AdditionalInputProps<R> = {
  className: string,
  id: string,
  disabled: boolean,
  ref: RefType<R>,
}

export type BaseInputProps<P extends object = InputHTMLAttributes<HTMLInputElement>, R extends unknown = HTMLInputElement> = {
  component?: string | FunctionComponent<P> | ComponentClass<P, any> | NamedExoticComponent<P>,
  ref?: RefType<R>,
  label?: ReactNode,
  isClearable?: boolean,
  warningText?: string | null,

  hasError?: boolean,
  errorText?: string | null,
  showErrorText?: boolean,
  showErrorIcon?: boolean,
  onErrorClick?: (e: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => void,
  clearOnErrorClick?: boolean,

  containerProps?: HtmlHTMLAttributes<HTMLDivElement>,
  focused?: boolean,
  className?: string,

  id?: string,
  disabled?: boolean,

  children?: ReactNode | ReactNode[],
  endAdornment?: ReactNode | ReactNode[],

  withFocusWithin?: boolean,
  onClear?: () => void,
  mapPropsToInput?: (props: { allProps: InputProps<P, R>, inputProps: P & AdditionalInputProps<R> }) => P,
  additionalOnBlurCheck?: (e: FocusEvent<HTMLDivElement>) => boolean
}

export type InputProps<P extends object = InputHTMLAttributes<HTMLInputElement>, R extends unknown = HTMLInputElement> =
  BaseInputProps<P, R> & P;

export const Input = genericMemo(forwardRef(<P extends object = InputHTMLAttributes<HTMLInputElement>, R extends unknown = HTMLInputElement>(
  props: InputProps<P, R>,
  ref: RefType<R>,
) => {
  const { value, onChange } = props as any as { value?: unknown, onChange?: (...args: Array<any>) => unknown };

  const {
    component = "input",
    containerProps, className, focused, label,
    children, endAdornment,

    isClearable = true, clearOnErrorClick = true, showErrorText = true, showErrorIcon = true, withFocusWithin = true,
    mapPropsToInput = (props) => props.inputProps, additionalOnBlurCheck,
    warningText, hasError, errorText, onErrorClick, onClear,
    id, disabled = false, ref: fakeRef,
    ...inputProps
  } = props;

  const realId = useMemo(() => id || v4(), [ id ]);
  const hasValue = `${value}` === "0" || !!value;
  const hasWarning = !!warningText;
  const isErrorIconClickable = hasValue && !disabled && (clearOnErrorClick || !!onErrorClick);
  const renderClearButton = hasValue && !disabled && isClearable && (!hasError || !isErrorIconClickable);
  const renderErrorButton = hasError && showErrorIcon;

  const containerOnBlur = containerProps?.onBlur;
  const onContainerBlur = useCallback((e: FocusEvent<HTMLDivElement>) => {
    if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget as Node)) {
      if (additionalOnBlurCheck) {
        if (additionalOnBlurCheck(e)) {
          return containerOnBlur?.(e);
        }
      } else {
        return containerOnBlur?.(e);
      }
    }
  }, [ containerOnBlur, additionalOnBlurCheck ]);
  const onClearButtonClick = useCallback(() => {
    if (!disabled) {
      if (onClear) {
        onClear();
      } else {
        return onChange?.(null);
      }
    }
  }, [ disabled, onClear, onChange ]);
  const onErrorButtonClick = useCallback((e: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (isErrorIconClickable) {
      if (onErrorClick) {
        onErrorClick(e);
      } else if (clearOnErrorClick) {
        onClearButtonClick();
      }
    }
  }, [ isErrorIconClickable, clearOnErrorClick, onClearButtonClick, onErrorClick ]);

  return (

    <div
      className={classnames(styles.container, className, {
        [styles.warning]: !hasError && hasWarning,
        [styles.error]: hasError,
        [styles.disabled]: disabled,
        [styles.focused]: focused,
        [styles.withFocusWithin]: withFocusWithin,
      })}
      title={typeof label === "string" ? label : undefined}
      {...containerProps}
      onBlur={onContainerBlur}
      data-input-disabled={disabled}
      data-input-error={!!hasError}
    >
      {label && <label htmlFor={id} className={styles.label}>{label}</label>}
      <div className={styles.root}>
        {children}
        {
          createElement(component, mapPropsToInput({
            allProps: props,
            inputProps: {
              ...inputProps as P,
              className: styles.input,
              id: realId,
              disabled,
              ref,
            },
          }))
        }
        {renderClearButton && <ClearButton onClick={onClearButtonClick} />}
        {renderErrorButton && <ErrorButton disabled={!isErrorIconClickable} onClick={onErrorButtonClick} />}
        {endAdornment}
      </div>
      {
        (hasError && showErrorText) ? (
          <span
            className={classnames(styles.helperText, styles.errorText)}
            title={errorText || ""}
            data-input-error-text={hasError}
          >
            {errorText}
          </span>
        ) : (
          hasWarning && <span className={classnames(styles.helperText, styles.warningText)} title={warningText}>{warningText}</span>
        )
      }
    </div>
  );
}) as InputComponentType);

export default Input;
